import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { homeContent, pageCopy, siteBaseUrl } from "../content/siteCopy";
import { brand } from "../brand";
import {
  getLocalizedService,
  getServiceById,
  getServiceIds,
  getServicePath,
  resolveServiceId,
} from "../services/catalog";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertJsonLd(schema) {
  const scriptId = "dynamic-structured-data";
  let script = document.getElementById(scriptId);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
}

function buildStaticRouteMap() {
  return [
    {
      type: "home",
      no: ["/", "/no", "/no/"],
      en: ["/en"],
      canonical: {
        no: "/no",
        en: "/en",
      },
    },
    {
      type: "services",
      no: ["/no/services"],
      en: ["/en/services"],
      canonical: {
        no: "/no/services",
        en: "/en/services",
      },
    },
    {
      type: "cases",
      no: ["/no/case", "/no/projects"],
      en: ["/en/case", "/en/projects"],
      canonical: {
        no: "/no/case",
        en: "/en/case",
      },
    },
    {
      type: "about",
      no: ["/no/om", "/no/about"],
      en: ["/en/om", "/en/about"],
      canonical: {
        no: "/no/om",
        en: "/en/om",
      },
    },
    {
      type: "contact",
      no: ["/no/kontakt", "/no/contact"],
      en: ["/en/kontakt", "/en/contact"],
      canonical: {
        no: "/no/kontakt",
        en: "/en/kontakt",
      },
    },
    {
      type: "book",
      no: ["/no/book"],
      en: ["/en/book"],
      canonical: {
        no: "/no/book",
        en: "/en/book",
      },
    },
    {
      type: "privacy",
      no: ["/no/personvern", "/no/privacy"],
      en: ["/en/personvern", "/en/privacy"],
      canonical: {
        no: "/no/personvern",
        en: "/en/personvern",
      },
    },
    {
      type: "products",
      no: ["/no/produkter"],
      en: ["/en/products"],
      canonical: {
        no: "/no/produkter",
        en: "/en/products",
      },
    },
  ];
}

function buildProfessionalSchema(language, pathname) {
  const localizedHome = pageCopy[language].home;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: brand.name,
        url: brand.baseUrl,
        logo: `${brand.baseUrl}${brand.assets.logoPng}`,
        image: `${brand.baseUrl}${brand.assets.socialImage}`,
        email: brand.contact.email,
        telephone: brand.contact.telephone,
        identifier: brand.orgNumberCompact,
        sameAs: [
          brand.social.linkedinCompany,
          brand.social.linkedinFounder,
          brand.social.github,
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "sales",
          email: brand.contact.email,
          telephone: brand.contact.telephone,
          areaServed: "NO",
          availableLanguage: ["Norwegian", "English"],
        },
      },
      {
        "@type": "ProfessionalService",
        name: brand.name,
        url: `${siteBaseUrl}${pathname}`,
        areaServed: "NO",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Oslo",
          addressCountry: "NO",
        },
        description: localizedHome.description,
        founder: {
          "@type": "Person",
          name: brand.founder,
        },
      },
    ],
  };
}

function buildHomeSchema(language) {
  const faqItems = homeContent[language].faq;
  const professionalGraph = buildProfessionalSchema(language, language === "no" ? "/no" : "/en")["@graph"];

  return {
    "@context": "https://schema.org",
    "@graph": [
      ...professionalGraph,
      {
        "@type": "WebSite",
        name: brand.name,
        url: brand.baseUrl,
        inLanguage: language === "no" ? "nb-NO" : "en",
        publisher: { "@type": "Organization", name: brand.name },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  };
}

function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${siteBaseUrl}${item.path}`,
    })),
  };
}

function buildServicesIndexSchema(language, pathname) {
  const isNorwegian = language === "no";
  const profGraph = buildProfessionalSchema(language, pathname)["@graph"];
  const items = getServiceIds().map((serviceId, index) => {
    const localized = getLocalizedService(serviceId, language);
    return {
      "@type": "ListItem",
      position: index + 1,
      name: localized.title,
      url: `${siteBaseUrl}${getServicePath(serviceId, language)}`,
    };
  });

  return {
    "@context": "https://schema.org",
    "@graph": [
      ...profGraph,
      {
        "@type": "ItemList",
        name: isNorwegian ? "Tjenester" : "Services",
        itemListElement: items,
      },
      buildBreadcrumbSchema([
        { name: isNorwegian ? "Hjem" : "Home", path: isNorwegian ? "/no" : "/en" },
        { name: isNorwegian ? "Tjenester" : "Services", path: pathname },
      ]),
    ],
  };
}

function buildServiceDetailSchema(serviceId, language) {
  const localized = getLocalizedService(serviceId, language);
  const isNorwegian = language === "no";
  const servicePath = getServicePath(serviceId, language);
  const profGraph = buildProfessionalSchema(language, servicePath)["@graph"];
  const servicesIndexPath = isNorwegian ? "/no/services" : "/en/services";

  return {
    "@context": "https://schema.org",
    "@graph": [
      ...profGraph,
      {
        "@type": "Service",
        name: localized.title,
        serviceType: localized.title,
        description: localized.teaser,
        url: `${siteBaseUrl}${servicePath}`,
        areaServed: { "@type": "Country", name: "Norway" },
        provider: {
          "@type": "Organization",
          name: brand.name,
          url: brand.baseUrl,
        },
        inLanguage: isNorwegian ? "nb-NO" : "en",
        category: isNorwegian
          ? "AI, data og operativ software"
          : "AI, data, and operational software",
        ...(Array.isArray(localized.deliverables) && localized.deliverables.length
          ? {
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: isNorwegian ? "Typiske leveranser" : "Typical deliverables",
                itemListElement: localized.deliverables.map((item) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: item },
                })),
              },
            }
          : {}),
      },
      buildBreadcrumbSchema([
        { name: isNorwegian ? "Hjem" : "Home", path: isNorwegian ? "/no" : "/en" },
        { name: isNorwegian ? "Tjenester" : "Services", path: servicesIndexPath },
        { name: localized.title, path: servicePath },
      ]),
    ],
  };
}

function buildContactSchema(language, pathname) {
  const isNorwegian = language === "no";
  const profGraph = buildProfessionalSchema(language, pathname)["@graph"];
  return {
    "@context": "https://schema.org",
    "@graph": [
      ...profGraph,
      {
        "@type": "ContactPage",
        url: `${siteBaseUrl}${pathname}`,
        inLanguage: isNorwegian ? "nb-NO" : "en",
      },
      buildBreadcrumbSchema([
        { name: isNorwegian ? "Hjem" : "Home", path: isNorwegian ? "/no" : "/en" },
        { name: isNorwegian ? "Kontakt" : "Contact", path: pathname },
      ]),
    ],
  };
}

function buildAboutSchema(language, pathname) {
  const isNorwegian = language === "no";
  const profGraph = buildProfessionalSchema(language, pathname)["@graph"];
  return {
    "@context": "https://schema.org",
    "@graph": [
      ...profGraph,
      {
        "@type": "AboutPage",
        url: `${siteBaseUrl}${pathname}`,
        inLanguage: isNorwegian ? "nb-NO" : "en",
      },
      buildBreadcrumbSchema([
        { name: isNorwegian ? "Hjem" : "Home", path: isNorwegian ? "/no" : "/en" },
        { name: isNorwegian ? "Om" : "About", path: pathname },
      ]),
    ],
  };
}

function getCanonicalForLanguage(pathname, language) {
  const staticRoutes = buildStaticRouteMap();
  const staticMatch = staticRoutes.find((route) => route.no.includes(pathname) || route.en.includes(pathname));
  if (staticMatch) {
    return staticMatch.canonical[language];
  }

  const servicePatterns = ["/no/services/:serviceId", "/en/services/:serviceId"];
  for (const pattern of servicePatterns) {
    const serviceMatch = matchPath(pattern, pathname);
    if (serviceMatch?.params?.serviceId) {
      const serviceId = resolveServiceId(serviceMatch.params.serviceId);
      if (serviceId) {
        return getServicePath(serviceId, language);
      }
    }
  }

  for (const serviceId of getServiceIds()) {
    const service = getServiceById(serviceId);
    const noDirectPath = `/no/${service.slugs.no}`;
    const enDirectPath = `/en/${service.slugs.en}`;
    if (pathname === noDirectPath || pathname === enDirectPath) {
      return language === "no" ? noDirectPath : enDirectPath;
    }
  }

  return language === "no" ? "/no" : "/en";
}

function getRouteMeta(pathname, language) {
  const copy = pageCopy[language];
  const isNorwegian = language === "no";

  const staticRoutes = [
    {
      match: isNorwegian ? ["/no", "/no/"] : ["/en"],
      meta: {
        ...copy.home,
        canonical: isNorwegian ? "/no" : "/en",
        schema: buildHomeSchema(language),
      },
    },
    {
      match: isNorwegian ? ["/no/services"] : ["/en/services"],
      meta: {
        ...copy.services,
        canonical: isNorwegian ? "/no/services" : "/en/services",
        schema: buildServicesIndexSchema(language, isNorwegian ? "/no/services" : "/en/services"),
      },
    },
    {
      match: isNorwegian ? ["/no/case", "/no/projects"] : ["/en/case", "/en/projects"],
      meta: {
        ...copy.cases,
        canonical: isNorwegian ? "/no/case" : "/en/case",
        schema: {
          "@context": "https://schema.org",
          "@graph": [
            ...buildProfessionalSchema(language, isNorwegian ? "/no/case" : "/en/case")["@graph"],
            buildBreadcrumbSchema([
              { name: isNorwegian ? "Hjem" : "Home", path: isNorwegian ? "/no" : "/en" },
              { name: isNorwegian ? "Case" : "Case studies", path: isNorwegian ? "/no/case" : "/en/case" },
            ]),
          ],
        },
      },
    },
    {
      match: isNorwegian ? ["/no/om", "/no/about"] : ["/en/om", "/en/about"],
      meta: {
        ...copy.about,
        canonical: isNorwegian ? "/no/om" : "/en/om",
        schema: buildAboutSchema(language, isNorwegian ? "/no/om" : "/en/om"),
      },
    },
    {
      match: isNorwegian ? ["/no/kontakt", "/no/contact"] : ["/en/kontakt", "/en/contact"],
      meta: {
        ...copy.contact,
        canonical: isNorwegian ? "/no/kontakt" : "/en/kontakt",
        schema: buildContactSchema(language, isNorwegian ? "/no/kontakt" : "/en/kontakt"),
      },
    },
    {
      match: isNorwegian ? ["/no/book"] : ["/en/book"],
      meta: {
        ...copy.book,
        canonical: isNorwegian ? "/no/book" : "/en/book",
        schema: buildContactSchema(language, isNorwegian ? "/no/book" : "/en/book"),
      },
    },
    {
      match: isNorwegian ? ["/no/personvern", "/no/privacy"] : ["/en/personvern", "/en/privacy"],
      meta: {
        ...copy.privacy,
        canonical: isNorwegian ? "/no/personvern" : "/en/personvern",
        schema: buildProfessionalSchema(language, isNorwegian ? "/no/personvern" : "/en/personvern"),
      },
    },
    {
      match: isNorwegian ? ["/no/produkter"] : ["/en/products"],
      meta: {
        ...copy.products,
        canonical: isNorwegian ? "/no/produkter" : "/en/products",
        schema: buildProfessionalSchema(language, isNorwegian ? "/no/produkter" : "/en/products"),
      },
    },
  ];

  const staticMatch = staticRoutes.find((route) => route.match.includes(pathname));
  if (staticMatch) return staticMatch.meta;

  const servicePattern = isNorwegian ? "/no/services/:serviceId" : "/en/services/:serviceId";
  const serviceMatch = matchPath(servicePattern, pathname);
  if (serviceMatch?.params?.serviceId) {
    const serviceId = resolveServiceId(serviceMatch.params.serviceId);
    if (serviceId) {
      const service = getLocalizedService(serviceId, language);
      return {
        title: `${service.title} | VImplement`,
        description: service.teaser,
        canonical: getServicePath(serviceId, language),
        schema: buildServiceDetailSchema(serviceId, language),
      };
    }
  }

  for (const serviceId of getServiceIds()) {
    const service = getServiceById(serviceId);
    const localized = getLocalizedService(serviceId, language);
    const directPath = language === "no" ? `/no/${service.slugs.no}` : `/en/${service.slugs.en}`;
    if (pathname === directPath) {
      return {
        title: `${localized.title} | VImplement`,
        description: localized.teaser,
        canonical: directPath,
        schema: buildServiceDetailSchema(serviceId, language),
      };
    }
  }

  // The home page is matched explicitly above, so reaching here means the URL is
  // unrecognized and App.js renders <NotFound/>. Keep the root/home variants
  // indexable as a safety net, but mark genuine soft-404s noindex so Google does
  // not index unknown URLs as if they were the homepage.
  const homeVariants = ["/", "/no", "/no/", "/en", "/en/"];
  if (homeVariants.includes(pathname)) {
    return {
      ...copy.home,
      canonical: isNorwegian ? "/no" : "/en",
      schema: buildProfessionalSchema(language, isNorwegian ? "/no" : "/en"),
    };
  }

  return {
    title: isNorwegian ? "Side ikke funnet | VImplement" : "Page not found | VImplement",
    description: copy.home.description,
    canonical: isNorwegian ? "/no" : "/en",
    robots: "noindex, follow",
    schema: buildProfessionalSchema(language, isNorwegian ? "/no" : "/en"),
  };
}

const RouteMetadata = ({ language }) => {
  const location = useLocation();

  useEffect(() => {
    const meta = getRouteMeta(location.pathname, language);
    const canonicalUrl = `${siteBaseUrl}${meta.canonical}`;
    const norwegianCanonical = `${siteBaseUrl}${getCanonicalForLanguage(location.pathname, "no")}`;
    const englishCanonical = `${siteBaseUrl}${getCanonicalForLanguage(location.pathname, "en")}`;

    document.title = meta.title;
    document.documentElement.lang = language === "no" ? "nb" : "en";

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: meta.description,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: meta.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: meta.description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: language === "no" ? "nb_NO" : "en_US",
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: brand.name,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: `${siteBaseUrl}${brand.assets.socialImage}`,
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: meta.title,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: meta.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: meta.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: `${siteBaseUrl}${brand.assets.socialImage}`,
    });
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: meta.title,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: meta.robots || "index, follow, max-image-preview:large, max-snippet:-1",
    });
    upsertLink('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });
    upsertLink('link[rel="alternate"][hreflang="nb-NO"]', {
      rel: "alternate",
      hreflang: "nb-NO",
      href: norwegianCanonical,
    });
    upsertLink('link[rel="alternate"][hreflang="en"]', {
      rel: "alternate",
      hreflang: "en",
      href: englishCanonical,
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: norwegianCanonical,
    });

    upsertJsonLd(meta.schema);
  }, [language, location.pathname]);

  return null;
};

export default RouteMetadata;
